import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import { useTranslation } from 'react-i18next';
import Button from '../../core/components/Button.tsx';
import Page from '../../core/components/Page';
import Layout from '../../core/components/Layout.tsx';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_CUSTOMER_SFTP_PUBLIC_KEY, DELETE_CUSTOMER_SFTP_PUBLIC_KEY, GET_SFTP_PUBLIC_KEYS } from '../queries.ts';
import { useToaster } from '../../core/components/Toast.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import classnames from '../../core/utils/classnames.tsx';
import Table, { RowControlIconButton } from '../../core/components/Table.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { faTrashCan, faCopy } from '@fortawesome/pro-regular-svg-icons';
import TextField from '../../core/components/Fields/TextField.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';

const DeleteIcon = withIcon(faTrashCan);
const CopyIcon = withIcon(faCopy);

const PUBLIC_KEY_FORM_DEFAULT_VALUES = {
    publicKeyBody: '',
};

const SFTP_SERVER_INFO = {
    host: 'ftp.workist.com',
    port: '22',
    rsaKeyFingerprint: 'LNe5eKZc2plvysmcr+1sZjfhjn63/K+L+0FtLbGjpXM',
    ipAddresses: ['63.34.88.144', '54.220.170.13', '52.49.47.193'],
};

const ServerInfoRow = ({
    label,
    value,
    copyValue,
    onCopy,
}: {
    label: string;
    value: React.ReactNode;
    copyValue?: string;
    onCopy?: () => void;
}) => (
    <div className="flex flex-col gap-1 md:flex-row md:items-start md:gap-4 py-3 border-b border-solid border-line-weak last:border-b-0">
        <div className="md:w-48 flex-shrink-0 text-sm font-medium text-content-strong">{label}</div>
        <div className="flex-1 flex items-start justify-between gap-2">
            <div className="font-mono text-sm text-content-strong break-all">{value}</div>
            {copyValue && onCopy && (
                <RowControlIconButton onClick={onCopy} aria-label="Copy">
                    <CopyIcon />
                </RowControlIconButton>
            )}
        </div>
    </div>
);

const PublicKeyRow = ({ publicKey, refetch }: { publicKey: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteSftpPublicKey] = useMutation(DELETE_CUSTOMER_SFTP_PUBLIC_KEY);
    const handleDeleteToken = async () => {
        try {
            await deleteSftpPublicKey({ variables: { publicKeyId: publicKey.SshPublicKeyId } });
            await refetch();
            publishToast({ description: t('sftpKey.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sftpKey.deleteError'), status: 'error' });
        }
    };

    return (
        <>
            <Table.Row>
                <Table.Cell className="font-mono break-all max-w-96">{publicKey.SshPublicKeyBody}</Table.Cell>
                <Table.Cell>{new Date(publicKey.DateImported).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <RowControlIconButton
                        className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                        onClick={handleDeleteToken}
                    >
                        <DeleteIcon />
                    </RowControlIconButton>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

const SftpPublicKeysPage = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: PUBLIC_KEY_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const {
        data: sftpPublicKeyData,
        loading,
        refetch,
    } = useQuery(GET_SFTP_PUBLIC_KEYS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const sftpPublicKeys = sftpPublicKeyData?.viewer?.customer?.sftpPublicKeys || [];
    const shortName = sftpPublicKeyData?.viewer?.customer?.shortName || '';

    const handleCopy = async (value: string, successMessage: string) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            publishToast({ description: successMessage, status: 'success' });
        } catch (e) {
            console.error(e);
        }
    };

    const [createSftpPublicKey] = useMutation(CREATE_CUSTOMER_SFTP_PUBLIC_KEY);
    const handleCreateToken = async (formData) => {
        try {
            await createSftpPublicKey({ variables: formData });
            await refetch();
            form.reset(PUBLIC_KEY_FORM_DEFAULT_VALUES);
            publishToast({ description: t('sftpKey.createSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sftpKey.createError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('sftpKey.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <SettingsFormCard title={t('sftpKey.serverInfo.title')}>
                        <p className="text-content-strong text-sm">{t('sftpKey.serverInfo.description')}</p>
                        <div className="flex flex-col border border-solid border-line-weak rounded-md px-4 bg-surface-default">
                            <ServerInfoRow
                                label={t('sftpKey.serverInfo.host')}
                                value={SFTP_SERVER_INFO.host}
                                copyValue={SFTP_SERVER_INFO.host}
                                onCopy={() => handleCopy(SFTP_SERVER_INFO.host, t('sftpKey.serverInfo.copied'))}
                            />
                            <ServerInfoRow
                                label={t('sftpKey.serverInfo.port')}
                                value={SFTP_SERVER_INFO.port}
                                copyValue={SFTP_SERVER_INFO.port}
                                onCopy={() => handleCopy(SFTP_SERVER_INFO.port, t('sftpKey.serverInfo.copied'))}
                            />
                            <ServerInfoRow
                                label={t('sftpKey.serverInfo.rsaKeyFingerprint')}
                                value={SFTP_SERVER_INFO.rsaKeyFingerprint}
                                copyValue={SFTP_SERVER_INFO.rsaKeyFingerprint}
                                onCopy={() =>
                                    handleCopy(SFTP_SERVER_INFO.rsaKeyFingerprint, t('sftpKey.serverInfo.copied'))
                                }
                            />
                            <ServerInfoRow
                                label={t('sftpKey.serverInfo.ipAddresses')}
                                value={
                                    <div className="flex flex-col gap-1">
                                        {SFTP_SERVER_INFO.ipAddresses.map((ip) => (
                                            <span key={ip}>{ip}</span>
                                        ))}
                                    </div>
                                }
                                copyValue={SFTP_SERVER_INFO.ipAddresses.join('\n')}
                                onCopy={() =>
                                    handleCopy(SFTP_SERVER_INFO.ipAddresses.join('\n'), t('sftpKey.serverInfo.copied'))
                                }
                            />
                            {shortName && (
                                <ServerInfoRow
                                    label={t('sftpKey.serverInfo.user')}
                                    value={shortName}
                                    copyValue={shortName}
                                    onCopy={() => handleCopy(shortName, t('sftpKey.username.copied'))}
                                />
                            )}
                        </div>
                    </SettingsFormCard>

                    {sftpPublicKeys && sftpPublicKeys.length > 0 && (
                        <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                            <Table.Head>
                                <Table.Row>
                                    <Table.HeadCell>{t('sftpKey.table.key')}</Table.HeadCell>
                                    <Table.HeadCell>{t('sftpKey.table.createdAt')}</Table.HeadCell>
                                    <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {sftpPublicKeys.map((publicKey) => (
                                    <PublicKeyRow key={publicKey.id} publicKey={publicKey} refetch={refetch} />
                                ))}
                            </Table.Body>
                        </Table>
                    )}

                    <SettingsFormCard onSubmit={form.handleSubmit(handleCreateToken)} title={t('sftpKey.form.title')}>
                        <p className="text-content-strong text-sm">{t('sftpKey.form.description')}</p>

                        <Controller
                            name="publicKeyBody"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sftpKey.form.publicKeyBody')} error={formErrors.publicKeyBody}>
                                    <TextField {...field} placeholder={t('sftpKey.form.publicKeyBodyPlaceholder')} />
                                </FormField>
                            )}
                        />

                        <div className="flex justify-end">
                            <Button type="submit" variant="primary">
                                {t('sftpKey.form.createButton')}
                            </Button>
                        </div>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default SftpPublicKeysPage;
